import * as React from "react";
import Proptypes from "prop-types";
import Carousel from "nuka-carousel";
import Img from "gatsby-image";
import { css } from "@emotion/core";
import { Link } from "@fitplan/lib/components/Link";
import { WebImage } from "@fitplan/lib/components/WebImage";
import { StaticQuery, graphql } from "gatsby";
import Feature from "./feature";
import { localeTextGetter } from "../../utils/l10n";
import config from "../../config";
import { Column } from "../primitives";
import CarouselButtonLeft from "../carouselButtons/CarouselButtonLeft";
import CarouselButtonRight from "../carouselButtons/CarouselButtonRight";

import "./appContent.scss";

const { locale, imageBaseUrl } = config;

/** AppContent
 * Displays the video/feature descriptions for fitplanapp
 * @example
 * <AppContent />
 *
 */
class AppContentRoot extends React.Component {
    state = {
        active: this.props.features[0].slug,
    };

    changeSlug = slug => this.setState({ active: slug });

    render() {
        const {
            titleText,
            subtitle,
            features,
            topComponent,
            bottomComponent,
        } = this.props;
        const updatedFeatures = [
            {
                "description": "Choose from dozens of programs designed by your favorite trainers. Work out at the gym, at home, or anywhere life takes you.",
                "slug": "fitness-plan",
                "feature": "Find your fitness plan",
                "image": {
                    "asset": {
                        "fluid": {
                            "base64": "data:image/png;base64,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",
                            "aspectRatio": 0.51,
                            "src": "/images/home/plans/fitplan-featuredplans.png?w=800&h=1569&fit=crop",
                            "srcSet": "/images/home/plans/fitplan-featuredplans.png?w=200&h=392&fit=crop 200w,\n/images/home/plans/fitplan-featuredplans.png?w=400&h=784&fit=crop 400w,\n/images/home/plans/fitplan-featuredplans.png?w=800&h=1569&fit=crop 800w,\n/images/home/plans/fitplan-featuredplans.png 918w",
                            "srcWebp": "/images/home/plans/fitplan-featuredplans.png?w=800&h=1569&fit=crop&fm=webp",
                            "srcSetWebp": "/images/home/plans/fitplan-featuredplans.png?w=200&h=392&fit=crop&fm=webp 200w,\n/images/home/plans/fitplan-featuredplans.png?w=400&h=784&fit=crop&fm=webp 400w,\n/images/home/plans/fitplan-featuredplans.png?w=800&h=1569&fit=crop&fm=webp 800w,\n/images/home/plans/fitplan-featuredplans.png?fm=webp 918w",
                            "sizes": "(max-width: 800px) 100vw, 800px"
                        }
                    }
                }
            },
            {
                "description": "Follow your daily workouts with HD step-by-step instructional videos, plus tools to track your weights, reps and time.",
                "slug": "track-progress",
                "feature": "Get Step-by-Step Instruction",
                "image": {
                    "asset": {
                        "fluid": {
                            "base64": "data:image/png;base64,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",
                            "aspectRatio": 0.51,
                            "src": "/images/home/plans/fitplan-exercise.png",
                            "srcSet": "/images/home/plans/fitplan-exercise.png?w=153&h=300&fit=crop 153w,\n/images/home/plans/fitplan-exercise.png?w=306&h=600&fit=crop 306w,\n/images/home/plans/fitplan-exercise.png 612w",
                            "srcWebp": "/images/home/plans/fitplan-exercise.png?fm=webp",
                            "srcSetWebp": "/images/home/plans/fitplan-exercise.png?w=153&h=300&fit=crop&fm=webp 153w,\n/images/home/plans/fitplan-exercise.png?w=306&h=600&fit=crop&fm=webp 306w,\n/images/home/plans/fitplan-exercise.png?fm=webp 612w",
                            "sizes": "(max-width: 612px) 100vw, 612px"
                        }
                    }
                }
            },
            {
                "description": "Visit the Feed for exclusive motivational content, plus fitness and nutrition tips, giveaways and more.",
                "slug": "your-best",
                "feature": "Stay Motivated",
                "image": {
                    "asset": {
                        "fluid": {
                            "base64": "data:image/png;base64,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",
                            "aspectRatio": 0.51,
                            "src": "/images/home/plans/fitplan-community.png",
                            "srcSet": "/images/home/plans/fitplan-community.png?w=153&h=300&fit=crop 153w,\n/images/home/plans/fitplan-community.png?w=306&h=600&fit=crop 306w,\n/images/home/plans/fitplan-community.png 612w",
                            "srcWebp": "/images/home/plans/fitplan-community.png?fm=webp",
                            "srcSetWebp": "/images/home/plans/fitplan-community.png?w=153&h=300&fit=crop&fm=webp 153w,\n/images/home/plans/fitplan-community.png?w=306&h=600&fit=crop&fm=webp 306w,\n/images/home/plans/fitplan-community.png?fm=webp 612w",
                            "sizes": "(max-width: 612px) 100vw, 612px"
                        }
                    }
                }
            }
        ]
        return (
            <Column className="app-features">
                {topComponent}
                <h1 className="app-features__title">{titleText}</h1>
                <p className="app-features__subtitle">{subtitle}</p>
                <div className="app__features-devices">
                    <Link
                        to={this.props.appleLink}
                        className="app__features-devices-badge"
                    >
                        <WebImage
                            className="app__features-devices-badge"
                            src={`${imageBaseUrl}/web/select-device/apple-badge.png`}
                            alt="apple badge"
                        />
                    </Link>
                    <Link
                        to={this.props.googleLink}
                        className="app__features-devices-badge"
                    >
                        <WebImage
                            className="app__features-devices-badge"
                            src={`${imageBaseUrl}/web/select-device/google-badge.png`}
                            alt="google badge"
                        />
                    </Link>
                </div>
                <div
                    css={css`
                        display: none;
                        @media (max-width: 900px) {
                            display: flex;
                            justify-content: center;
                            width: 100%;
                        }
                    `}
                >
                    <Carousel
                        enableKeyboardControls={false}
                        disableKeyboardControls
                        slidesToShow={1}
                        slidesToScroll="auto"
                        wrapAround
                        framePadding={"0px"}
                        renderBottomCenterControls={() => null}
                        renderCenterLeftControls={({ previousSlide }) => (
                            <CarouselButtonLeft
                                previousSlide={previousSlide}
                                className="app-features__carousel-left"
                            />
                        )}
                        renderCenterRightControls={({ nextSlide }) => (
                            <CarouselButtonRight
                                nextSlide={nextSlide}
                                className="app-features__carousel-right"
                            />
                        )}
                    >
                        {updatedFeatures.map(feature => (
                            <section
                                key={feature.slug}
                                className="app__features-mobile"
                            >
                                <div className="app__features-screenshot">
                                    <Img
                                        critical
                                        fluid={feature.image.asset.fluid}
                                        alt={feature.slug}
                                        key={feature.slug}
                                    />
                                </div>
                                <Feature
                                    {...feature}
                                    className="app-feature"
                                    onInteract={() => null}
                                />
                            </section>
                        ))}
                    </Carousel>
                </div>
                <div
                    css={css`
                        display: none;
                        align-items: center;
                        @media (min-width: 901px) {
                            display: flex;
                        }
                    `}
                >
                    <section>
                        {updatedFeatures.map((feature, i) => (
                            <Feature
                                {...feature}
                                key={feature.slug}
                                onInteract={this.changeSlug}
                                active={this.state.active}
                            />
                        ))}
                    </section>
                    <section>
                        {updatedFeatures.map((feature, i) => (
                            <div
                                className="app__features-screenshot"
                                style={{
                                    display:
                                        feature.slug === this.state.active
                                            ? "block"
                                            : "none",
                                }}
                            >
                                <Img
                                    critical
                                    fluid={feature.image.asset.fluid}
                                    alt={feature.feature}
                                />
                            </div>
                        ))}
                    </section>
                </div>
                {bottomComponent}
            </Column>
        );
    }
}

const AppContent = props => (
    <StaticQuery
        query={graphql`
            query appContentQuery {
                sanityAppContentSection {
                    buttonText {
                        _type
                        en
                        es
                    }
                    subtitle {
                        _type
                        en
                        es
                    }
                    titleText {
                        _type
                        en
                        es
                    }
                    features {
                        description {
                            _type
                            en
                            es
                        }
                        slug
                        feature {
                            _type
                            en
                            es
                        }
                        image {
                            asset {
                                fluid {
                                    ...GatsbySanityImageFluid
                                }
                            }
                        }
                    }
                }
                sanitySelectDevice {
                    apple
                    google
                }
            }
        `}
        render={data => {
            const {
                sanityAppContentSection: {
                    titleText,
                    features,
                    buttonText,
                    subtitle,
                },
                sanitySelectDevice: { apple, google },
            } = localeTextGetter(data);
            return (
                <AppContentRoot
                    {...props}
                    locale={locale}
                    titleText={titleText}
                    buttonText={buttonText}
                    features={features}
                    subtitle={subtitle}
                    appleLink={apple}
                    googleLink={google}
                />
            );
        }}
    />
);

AppContentRoot.propTypes = {
    features: Proptypes.shape({
        feature1: Proptypes.shape({
            description: Proptypes.string.isRequired,
            slug: Proptypes.string.isRequired,
            feature: Proptypes.string.isRequired,
        }),
        feature2: Proptypes.shape({
            description: Proptypes.string.isRequired,
            slug: Proptypes.string.isRequired,
            feature: Proptypes.string.isRequired,
        }),
        feature3: Proptypes.shape({
            description: Proptypes.string.isRequired,
            slug: Proptypes.string.isRequired,
            feature: Proptypes.string.isRequired,
        }),
        feature4: Proptypes.shape({
            description: Proptypes.string.isRequired,
            slug: Proptypes.string.isRequired,
            feature: Proptypes.string.isRequired,
        }),
    }).isRequired,
    locale: Proptypes.string.isRequired,
    titleText: Proptypes.string.isRequired,
    buttonText: Proptypes.string.isRequired,
};

export default AppContent;
